export const BatteryChemistry = {
  LI_FEPO4: 'LI_FEPO4',
  LI_ION: 'LI_ION',
  LEAD_ACID_FLOODED: 'LEAD_ACID_FLOODED',
  AGM: 'AGM',
  GEL: 'GEL',
  NI_MH: 'NI_MH',
  NI_CD: 'NI_CD',
  ALKALINE: 'ALKALINE',
  CUSTOM: 'CUSTOM',
} as const
export type BatteryChemistry = (typeof BatteryChemistry)[keyof typeof BatteryChemistry]

export const CapacityUnit = {
  MAH: 'MAH',
  AH: 'AH',
  WH: 'WH',
  KWH: 'KWH',
} as const
export type CapacityUnit = (typeof CapacityUnit)[keyof typeof CapacityUnit]

export const LoadUnit = {
  MA: 'MA',
  A: 'A',
  MW: 'MW',
  W: 'W',
  KW: 'KW',
} as const
export type LoadUnit = (typeof LoadUnit)[keyof typeof LoadUnit]

export const VoltageUnit = {
  V: 'V',
  MV: 'MV',
} as const
export type VoltageUnit = (typeof VoltageUnit)[keyof typeof VoltageUnit]

export const TimeUnit = {
  HOURS: 'HOURS',
  MINUTES: 'MINUTES',
  SECONDS: 'SECONDS',
  DAYS: 'DAYS',
} as const
export type TimeUnit = (typeof TimeUnit)[keyof typeof TimeUnit]

export const SolveMode = {
  RUNTIME: 'RUNTIME',
  BATTERY_SIZE: 'BATTERY_SIZE',
  MAX_LOAD: 'MAX_LOAD',
} as const
export type SolveMode = (typeof SolveMode)[keyof typeof SolveMode]

export const CalculationMode = {
  CURRENT_BASED: 'CURRENT_BASED',
  POWER_BASED: 'POWER_BASED',
} as const
export type CalculationMode = (typeof CalculationMode)[keyof typeof CalculationMode]

export const TemperatureUnit = {
  CELSIUS: 'CELSIUS',
  FAHRENHEIT: 'FAHRENHEIT',
} as const
export type TemperatureUnit = (typeof TemperatureUnit)[keyof typeof TemperatureUnit]

export interface TemperatureDeratingPoint {
  tempC: number
  factor: number
}

export interface ChemistryPreset {
  chemistry: BatteryChemistry
  nominalCellVoltage: number
  maxDoD: number
  defaultDoD: number
  peukertExponent: number
  maxSafeCRate: number
  peakCRate: number
  cutoffVoltage: number
  minTempC: number
  maxTempC: number
  selfDischargePerDay: number
  cycleLifeRange: string
  ratedDischargeHours: number
  deratingCurve: TemperatureDeratingPoint[]
}

export interface BatteryInput {
  chemistry: BatteryChemistry
  capacityValue: number
  capacityUnit: CapacityUnit
  voltage: number
  dod: number
  stateOfCharge: number
  stateOfHealth: number
  temperatureC: number
  cutoffVoltage: number
  ageCycles: number
  storageDays: number
}

export interface LoadEntry {
  id: string
  name: string
  loadValue: number
  loadUnit: LoadUnit
  enabled: boolean
  isPreset: boolean
  presetId?: string
}

export interface DutyCycleInput {
  activeCurrent: number
  sleepCurrent: number
  activeDurationMs: number
  sleepDurationMs: number
  useCustomDutyCycle: boolean
}

export interface LoadInput {
  loads: LoadEntry[]
  dutyCycle?: DutyCycleInput
}

export interface EfficiencyInput {
  efficiencyPercent: number
  includeEfficiency: boolean
  description: string
}

export interface PackConfig {
  cellsInSeries: number
  cellsInParallel: number
  singleCellCapacity: number
  singleCellVoltage: number
  chemistry: BatteryChemistry
}

export interface SafetyInput {
  reserveMarginPercent: number
  includeReserve: boolean
  minimumRuntimeTarget: number
  includeTargetCheck: boolean
}

export interface ChargerInput {
  chargerCurrentValue: number
  chargerVoltage: number
  chargerPower: number
  chargeEfficiency: number
}

export interface CalibrationInput {
  measuredRuntimeHours: number
  calibrationDate: string
  calibrationFactor: number
}

export interface ScenarioOverrides {
  chemistry?: BatteryChemistry
  capacityValue?: number
  voltage?: number
  dod?: number
  stateOfCharge?: number
  stateOfHealth?: number
  temperatureC?: number
  loadValue?: number
  efficiencyPercent?: number
}

export interface Scenario {
  id: string
  name: string
  batteryOverrides: ScenarioOverrides
  loadOverrides: { loadValue?: number; loadUnit?: LoadUnit } | null
  efficiencyOverrides: { efficiencyPercent?: number; includeEfficiency?: boolean } | null
}

export interface SavedBatteryProfile {
  id: string
  name: string
  chemistry: BatteryChemistry
  capacityValue: number
  capacityUnit: CapacityUnit
  voltage: number
  dod: number
  stateOfCharge: number
  stateOfHealth: number
}

export interface SavedLoadProfile {
  id: string
  name: string
  loads: LoadEntry[]
  dutyCycle?: DutyCycleInput
  createdAt: string
  updatedAt: string
  usageCount: number
  tags: string[]
  isFavorite: boolean
}

export interface DevicePreset {
  id: string
  name: string
  category: string
  loadValue: number
  loadUnit: LoadUnit
  voltage?: number
  description: string
}

export interface UserPreferences {
  defaultChemistry: BatteryChemistry
  defaultVoltage: number
  defaultLoadPreset: string | null
  locale: string
  temperatureUnit: TemperatureUnit
  dischargeCurveResolution: 50 | 100 | 200
  showAdvancedModeDefault: boolean
  numberFormat: 'system' | 'fixed' | 'scientific'
  showSecondsAlways: boolean
  defaultEfficiency: number
  defaultReserveMargin: number
  autoSaveProfiles: boolean
  historyLimit: number
  exportFormat: 'pdf' | 'csv' | 'excel'
  textSize: 'normal' | 'large'
  contrastMode: 'normal' | 'high'
}

export type ConfidenceLevel = 'ideal' | 'adjusted' | 'approximate'

export type CRateStatus = 'safe' | 'caution' | 'danger'

export type PassFailStatus = 'pass' | 'marginal' | 'fail'

export interface RuntimeResult {
  runtimeHours: number | null
  error: string | null
  rawCapacityAh: number
  effectiveCapacityAh: number
  effectiveEnergyWh: number
  totalLoadA: number
  totalLoadW: number
  cRate: number
  calculationMode: CalculationMode
  deratingSteps: {
    afterDoD: number
    afterSoC: number
    afterSoH: number
    afterTemperature: number
    afterSelfDischarge: number
    afterPeukert: number
    afterEfficiency: number
    afterReserve: number
  }
  deratingFactors: {
    dodFactor: number
    socFactor: number
    sohFactor: number
    temperatureFactor: number
    selfDischargeFactor: number
    peukertFactor: number
    efficiencyFactor: number
    reserveFactor: number
  }
  confidence: ConfidenceLevel
  confidenceExplanation: string
  formulaExplanation: string
  assumptionsList: string[]
  runtimeFormatted: string
  runtimeMinutes: number | null
  runtimeSeconds: number | null
  effectiveLoadA: number
  effectiveLoadW: number
}

export interface NearestSize {
  capacityAh: number
  label: string
  estimatedRuntimeHours: number
  passFail: PassFailStatus
}

export interface BatterySizeResult {
  requiredCapacityAh: number | null
  error: string | null
  effectiveLoadA: number
  targetRuntimeHours: number
  totalDeratingFactor: number
  nearestSizes: NearestSize[]
}

export interface MaxLoadResult {
  maxLoadA: number | null
  maxLoadW: number | null
  error: string | null
  targetRuntimeHours: number
}

export interface CRateValidationResult {
  cRate: number
  maxSafeCRate: number
  peakCRate: number
  status: CRateStatus
  recommendation: string
}

export interface PackBuilderResult {
  totalVoltage: number
  totalCapacityAh: number
  totalEnergyWh: number
  totalCells: number
  configuration: string
  warnings: string[]
  packConfig: PackConfig
}

export interface DutyCycleResult {
  averageCurrentA: number
  dutyCycleRatio: number
  effectiveLoadA: number
  description: string
}

export interface DischargeCurvePoint {
  timeHours: number
  socPercent: number
  voltage: number
  isKneePoint: boolean
}

export interface ChargeTimeResult {
  chargeTimeHours: number | null
  error: string | null
  capacityToReplenishAh: number
  chargeRateC: number
  chargeMethod: 'current' | 'power'
}

export interface SensitivityPoint {
  inputValue: number
  runtimeHours: number
  deltaFromBaseline: number
  label: string
}

export interface SensitivityResult {
  variable: string
  baselineRuntimeHours: number
  dataPoints: SensitivityPoint[]
  insightText: string
}

export interface ScenarioResultEntry {
  scenarioId: string
  scenarioName: string
  runtimeHours: number | null
  error: string | null
  effectiveCapacityAh: number
  totalLoadA: number
  cRate: number
  deltaFromBaselineHours: number | null
}

export interface ScenarioComparison {
  baselineRuntimeHours: number | null
  scenarios: ScenarioResultEntry[]
}

export interface PassFailResult {
  status: PassFailStatus
  runtimeHours: number | null
  targetRuntimeHours: number
  marginHours: number | null
  marginPercent: number | null
  recommendation: string
}

export interface HistoryEntry {
  id: string
  timestamp: string
  summary: string
  battery: BatteryInput
  load: LoadInput
  efficiency: EfficiencyInput
  safety: SafetyInput
  solveMode: SolveMode
  runtimeResult: RuntimeResult | null
}

export interface PersistedState {
  version: number
  battery: BatteryInput
  solveMode: SolveMode
  calculationMode: CalculationMode
  load: LoadInput
  charger: ChargerInput | null
  efficiency: EfficiencyInput
  safety: SafetyInput
  scenarios: Scenario[]
  pack: PackConfig | null
  preferences: UserPreferences
  ui: {
    activeTab: string
    theme: string
    showFormulaExplanation: boolean
    showAssumptionsSummary: boolean
    showAdvancedMode: boolean
  }
  savedBatteryProfiles: SavedBatteryProfile[]
  savedLoadProfiles: SavedLoadProfile[]
  calculationHistory: HistoryEntry[]
  calibration: CalibrationInput | null
  userTier: 'free' | 'pro'
}
